import { useState, useRef } from "react";

const MODES = { FRESH: "fresh", REWRITE: "rewrite", REVISE: "revise" };
const LANGUAGES = ["UK English", "US English"];
const WORD_COUNTS = ["400", "600", "800", "1000", "1200", "1500"];
const API_KEY = process.env.REACT_APP_ANTHROPIC_KEY;

// ─── HUMAN VERSION SYSTEM PROMPT ────────────────────────────────────────────
function buildHumanSystemPrompt(date) {
  return `You are an experienced recruitment consultant with deep expertise in your specialisation. You write blogs for a recruitment agency directed at professionals in your field — people who know their industry well. Your writing voice is relaxed, confident and insightful. You're passionate about the field without being promotional. You speak to peers, not pupils.

Today's date is: ${date}

Your writing rules, non-negotiable:
- Use contractions and natural phrasing throughout
- Mix sentence lengths for rhythm — short punchy sentences alongside longer ones
- Address the reader directly with "you" language
- Use subheadings that sound like something a person would say, not an academic title
- Include rhetorical questions to pull the reader in
- Ground everything in real-world scenarios the reader will recognise
- Be honest about challenges without being discouraging
- Any statistics, salaries, market data or trends must reflect the current date above
- Ignore any dates, statistics or figures from any reference material provided — replace with current accurate data
- Do not use emoji or em dashes
- Do not use corporate jargon or cliched expressions like "in today's fast-paced world" or "in the world of"
- Do not quote or reference any other recruitment agencies
- Do not write a section titled "Conclusion" — end naturally
- Do not patronise the reader — assume they know their field better than you do
- Write in the language variant specified in each request — apply it consistently. Never mix variants

HUMAN AUTHENTICITY — THIS IS THE MOST IMPORTANT SECTION. FAILURE HERE MEANS THE BLOG FAILS:
The single biggest giveaway of AI writing is smoothness. Every section doing its job perfectly. Every paragraph landing cleanly. No rough edges. You must deliberately break this pattern.
- Do NOT write a clean arc. Real bloggers meander. One section should do two things at once, or veer slightly off topic before pulling back
- NEVER use the word "genuinely" — it is the single most common AI tell word
- NEVER preemptively address scepticism with phrases like "this time it's different" or "this isn't hype" — that rehearsed quality is an immediate AI signal
- Include one opinion that goes slightly too far — something a cautious editor would want to soften. Real people have edges
- Add one oddly specific detail that only someone with real experience would know — a specific site, an obscure programme name, a precise figure that feels lived-in rather than researched
- Vary paragraph lengths dramatically and unpredictably — one paragraph must be a single sentence. One must be noticeably longer than everything else
- Let one paragraph trail off without a clean resolution — real writers do not always land every point
- Salary or market data must be woven into the narrative, not presented as a standalone paragraph. It should feel like something mentioned in passing, not a prompted insert
- The closing must not feel like a CTA. It should feel like the writer ran out of steam in a natural way and mentioned getting in touch almost as an afterthought
- Include at least one sentence that is slightly too long or structured awkwardly — the kind a copy editor would flag but a real writer would leave in
- Read the output back before returning it. If every section lands cleanly and there are no rough edges, rewrite until there are`;
}

// ─── AI-FRIENDLY VERSION SYSTEM PROMPT ──────────────────────────────────────
function buildAISystemPrompt(date) {
  return `You are an expert content writer who specialises in creating AI-optimised blog content. Your blogs are structured specifically to rank well in AI answer engines (ChatGPT, Perplexity, Google AI Overviews), voice search, and featured snippets, while remaining genuinely useful and readable for humans.

Today's date is: ${date}

Your formatting rules, non-negotiable:
- Use question-based H2 headings (e.g. "What is X?", "How does Y work?", "Why does Z matter?")
- Answer each question immediately in the first 1-2 sentences — AI engines extract the first clear answer
- Start major sections with a bold "Key takeaway:" line that summarises the section in one sentence
- Keep paragraphs to 2-3 sentences maximum — short paragraphs are easier for AI to parse and extract
- Include one numbered step-by-step "how-to" process section
- Use bullet points for lists of 3 or more items
- End the blog with exactly 5 FAQ pairs in a dedicated FAQ section — questions must be ones real people search for
- Include a TL;DR summary section near the end before the FAQ
- Use simple, jargon-free language — explain any technical terms immediately after using them
- Any statistics, examples or figures must be real and verifiable — do not fabricate data
- Include 2-3 specific real-world examples with actual numbers or outcomes
- Write in the language variant specified

Content structure every AI-friendly blog must follow:
1. Opening paragraph — answer the core question immediately, no preamble
2. 3-5 H2 question sections each with Key takeaway, clear answer, and supporting detail
3. One how-to numbered process section
4. TL;DR summary (5-7 bullet points covering the whole blog)
5. FAQ section (5 question-answer pairs, 2-4 sentences per answer)
6. Brief closing with a natural mention of getting in touch

Optimisation rules:
- The blog title must be a question or contain the primary keyword naturally
- First paragraph must contain the primary topic keyword
- Each H2 must be a complete question a human would actually type into a search engine
- Answers must be self-contained — a reader (or AI) should understand each section without reading the rest`;
}

// ─── HUMAN VERSION USER PROMPT ──────────────────────────────────────────────
function buildHumanUserPrompt(mode, fields) {
  const { topic, audience, wordCount, language, notes, referenceBlog, originalBlog, revisionInstructions, sourceMaterial, recruitmentSection, writingPerson } = fields;
  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  if (mode === MODES.REVISE) {
    return `Language variant: ${language}
REVISION REQUEST: ${revisionInstructions}
Original blog to revise:
${originalBlog}
Instructions: Do not write a new blog. Apply exactly what was asked. Keep everything else intact. Maintain language variant throughout. Return full revised blog.
Return in this exact format:
Title:
Meta description (155 characters max):
Blog body:`;
  }

  const sourceMaterialBlock = (mode === MODES.FRESH && sourceMaterial)
    ? `SOURCE MATERIAL:\n${sourceMaterial}\nInstructions: Extract facts, data and angles. Do not copy phrasing. Update any outdated stats as of ${today}.` : "";

  const referenceBlock = referenceBlog
    ? `REFERENCE BLOG:\n${referenceBlog}\nInstructions: Use as inspiration only. Write entirely from scratch. Replace all outdated figures with current data as of ${today}.` : "";

  const recruitmentBlock = recruitmentSection
    ? `RECRUITMENT SECTION REQUIRED: Weave in a natural section covering current hiring market in this field, in-demand skills, salary ranges, and notable career shifts. Base all figures on ${today}. Keep peer-to-peer voice — not a job advert.` : "";

  const personBlock = writingPerson === "first"
    ? `Writing style: First person. Use I, we, my, our naturally. Include personal observations and direct opinions as your own.`
    : `Writing style: Generic authoritative tone. Do not use I or we.`;

  return `Write a blog post for a recruitment agency.
Topic: ${topic || "Extract from reference blog"}
Target audience: ${audience || "Extract from reference blog"}
Approximate word count: ${wordCount}
Language variant: ${language}
${personBlock}
Specific angles: ${notes || "None"}
${sourceMaterialBlock}
${referenceBlock}
${recruitmentBlock}

Structure: Strong opening hook (no "I", "In the world of", or dictionary definition). 3-5 conversational subheadings. Natural closing that mentions getting in touch without sounding like a CTA.

Return in this exact format:
Title:
Meta description (155 characters max):
Blog body:`;
}

// ─── AI-FRIENDLY VERSION USER PROMPT ────────────────────────────────────────
function buildAIUserPrompt(fields) {
  const { topic, audience, wordCount, language, notes, sourceMaterial, recruitmentSection } = fields;
  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  const sourceMaterialBlock = sourceMaterial
    ? `SOURCE MATERIAL:\n${sourceMaterial}\nExtract real facts, figures and examples from this. Do not copy phrasing. Verify and update any outdated stats as of ${today}.` : "";

  const recruitmentBlock = recruitmentSection
    ? `RECRUITMENT SECTION: Include a question-based section (e.g. "What does this mean for your career?") covering hiring trends, in-demand skills, and salary ranges in this field as of ${today}.` : "";

  return `Write an AI-optimised blog post following the exact structure in your instructions.

Topic: ${topic}
Target audience: ${audience || "professionals in this field"}
Approximate word count: ${wordCount}
Language variant: ${language}
Additional angles: ${notes || "None"}
${sourceMaterialBlock}
${recruitmentBlock}

Requirements:
- Title must be a question or contain the primary keyword
- Open by answering the core question immediately — no preamble
- Use question-based H2 headings throughout
- Bold "Key takeaway:" at the start of each major section
- Include one numbered how-to process
- End with TL;DR (5-7 bullets) then 5 FAQ pairs
- All examples and statistics must be real — do not fabricate

Return in this exact format:
Title:
Meta description (155 characters max):
Blog body:`;
}

function parseBlogOutput(raw) {
  const titleMatch = raw.match(/Title:\s*(.+)/);
  const metaMatch = raw.match(/Meta description[^:]*:\s*(.+)/);
  const bodyMatch = raw.match(/Blog body:\s*([\s\S]+)/);
  return {
    title: titleMatch ? titleMatch[1].trim() : "",
    meta: metaMatch ? metaMatch[1].trim() : "",
    body: bodyMatch ? bodyMatch[1].trim() : raw,
  };
}

function downloadWord(blog) {
  const { title, meta, body } = blog;
  const esc = s => s
    .replace(/\\/g, "\\\\")
    .replace(/\{/g, "\\{")
    .replace(/\}/g, "\\}")
    .replace(/[\u0080-\uFFFF]/g, c => "\\u" + c.charCodeAt(0) + "?");
  let rtf = "{\\rtf1\\ansi\\deff0\n";
  rtf += "{\\fonttbl{\\f0\\froman\\fcharset0 Calibri;}}\n";
  rtf += "\\widowctrl\\wpaper15840\\wpaperw12240\\margl1800\\margr1800\\margt1440\\margb1440\n";
  rtf += "{\\pard\\sb240\\sa120\\b\\f0\\fs36 " + esc(title) + "\\par}\n";
  rtf += "{\\pard\\sb0\\sa240\\i\\f0\\fs18 Meta: " + esc(meta) + "\\par}\n";
  body.split(/\n\n+/).forEach(para => {
    const p = para.trim();
    if (!p) return;
    if (p.startsWith("## ") || p.startsWith("# ")) {
      rtf += "{\\pard\\sb240\\sa120\\b\\f0\\fs28 " + esc(p.replace(/^#{1,2} /, "")) + "\\par}\n";
    } else if (p.startsWith("**") && p.endsWith("**")) {
      rtf += "{\\pard\\sb120\\sa80\\b\\f0\\fs22 " + esc(p.replace(/\*\*/g, "")) + "\\par}\n";
    } else {
      rtf += "{\\pard\\sb0\\sa160\\f0\\fs22 " + esc(p.replace(/\n/g, " ")) + "\\par}\n";
    }
  });
  rtf += "}";
  const blob = new Blob([rtf], { type: "application/rtf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = title.replace(/[^a-z0-9]/gi, "_").toLowerCase() + ".rtf";
  a.click();
  URL.revokeObjectURL(url);
}

function InlineRevise({ rawOutput, language, onRevised, systemPrompt }) {
  const [instructions, setInstructions] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [revisionCount, setRevisionCount] = useState(0);

  const handleRevise = async () => {
    if (!instructions.trim()) return setError("Please describe what you'd like changed.");
    setError("");
    setLoading(true);
    const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    try {
      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-api-key": API_KEY, "anthropic-version": "2023-06-01", "anthropic-dangerous-direct-browser-access": "true" },
        body: JSON.stringify({
          model: "claude-opus-4-6", max_tokens: 4000,
          system: systemPrompt,
          messages: [{ role: "user", content: `Language variant: ${language}\nREVISION REQUEST: ${instructions}\n\nOriginal blog:\n${rawOutput}\n\nApply exactly what was asked. Keep everything else intact. Return full blog.\n\nReturn in this exact format:\nTitle:\nMeta description (155 characters max):\nBlog body:` }],
        }),
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error.message);
      onRevised(data.content[0].text);
      setInstructions("");
      setRevisionCount(c => c + 1);
    } catch (e) {
      setError(e.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: "#fff", borderRadius: 14, padding: 28, boxShadow: "0 2px 16px rgba(0,0,0,0.07)", border: "1.5px dashed #38bdf8", marginBottom: 40 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <div style={{ width: 28, height: 28, background: "#0ea5e9", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ color: "#fff", fontSize: 14 }}>✎</span>
        </div>
        <div>
          <div style={{ fontFamily: "sans-serif", fontSize: 13, fontWeight: 700, color: "#1a1a2e" }}>
            Revise this blog
            {revisionCount > 0 && <span style={{ marginLeft: 8, background: "#0ea5e9", color: "#fff", borderRadius: 10, padding: "1px 8px", fontSize: 11 }}>{revisionCount} revision{revisionCount > 1 ? "s" : ""} applied</span>}
          </div>
          <div style={{ fontFamily: "sans-serif", fontSize: 12, color: "#999" }}>Describe exactly what you want changed — the rest stays as is</div>
        </div>
      </div>
      <textarea rows={3} placeholder='e.g. "The opening feels flat" or "Shorten the third section" or "Soften the call to action"'
        value={instructions} onChange={e => setInstructions(e.target.value)}
        style={{ width: "100%", padding: "11px 14px", borderRadius: 9, border: "1.5px solid #ddd", fontSize: 14, fontFamily: "sans-serif", outline: "none", color: "#1a1a2e", background: "#fafafa", resize: "vertical", boxSizing: "border-box", marginBottom: 12 }} />
      {error && <div style={{ background: "#fff4f4", border: "1px solid #f5c6c6", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontFamily: "sans-serif", fontSize: 13, color: "#c0392b" }}>{error}</div>}
      <button onClick={handleRevise} disabled={loading}
        style={{ padding: "11px 28px", background: loading ? "#aaa" : "#0ea5e9", color: "#fff", border: "none", borderRadius: 9, fontFamily: "sans-serif", fontSize: 14, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer" }}>
        {loading ? "Revising..." : "Apply Revision"}
      </button>
      {loading && <span style={{ marginLeft: 14, fontFamily: "sans-serif", fontSize: 13, color: "#888" }}>Updating your blog...<style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></span>}
    </div>
  );
}

function BlogOutput({ blog, rawOutput, language, onRevised, systemPrompt }) {
  return (
    <div>
      <div style={{ background: "#fff", borderRadius: 14, padding: 36, boxShadow: "0 2px 16px rgba(0,0,0,0.07)", border: "1px solid #e8e4dc", marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <div style={{ fontFamily: "sans-serif", fontSize: 11, fontWeight: 700, color: "#0ea5e9", letterSpacing: 1.5, textTransform: "uppercase" }}>Your Blog</div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => navigator.clipboard.writeText(rawOutput)}
              style={{ padding: "9px 18px", background: "#f7f5f0", border: "1.5px solid #ddd", borderRadius: 8, fontFamily: "sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#444" }}>
              Copy Text
            </button>
            <button onClick={() => downloadWord(blog)}
              style={{ padding: "9px 18px", background: "#0ea5e9", border: "none", borderRadius: 8, fontFamily: "sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#fff" }}>
              Download Word Doc
            </button>
          </div>
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: "#1a1a2e", marginBottom: 10, lineHeight: 1.3 }}>{blog.title}</h1>
        <div style={{ background: "#f7f5f0", borderLeft: "3px solid #38bdf8", padding: "10px 16px", borderRadius: "0 8px 8px 0", marginBottom: 28, fontFamily: "sans-serif", fontSize: 13, color: "#666", lineHeight: 1.5 }}>
          <strong style={{ color: "#1a1a2e" }}>Meta:</strong> {blog.meta}
        </div>
        <div style={{ fontSize: 16, lineHeight: 1.85, color: "#2a2a2a" }}>
          {blog.body.split(/\n\n+/).map((para, i) => {
            const p = para.trim();
            if (p.startsWith("## ") || p.startsWith("# ")) return <h2 key={i} style={{ fontSize: 19, fontWeight: 700, color: "#1a1a2e", margin: "28px 0 10px", lineHeight: 1.3 }}>{p.replace(/^#{1,2} /, "")}</h2>;
            if (p.startsWith("**Key takeaway:**") || p.startsWith("**Key takeaway:")) return <p key={i} style={{ marginBottom: 12, padding: "8px 14px", background: "#f0f9ff", borderLeft: "3px solid #0ea5e9", borderRadius: "0 6px 6px 0", fontFamily: "sans-serif", fontSize: 14 }} dangerouslySetInnerHTML={{ __html: p.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") }} />;
            if (p.startsWith("**TL;DR") || p.startsWith("## TL;DR") || p.startsWith("TL;DR")) return <div key={i} style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, padding: "14px 18px", marginBottom: 16, fontFamily: "sans-serif", fontSize: 14 }}><strong style={{ color: "#0369a1" }}>TL;DR</strong><div dangerouslySetInnerHTML={{ __html: p.replace(/TL;DR[:\s]*/i, "").replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") }} /></div>;
            return <p key={i} style={{ marginBottom: 16 }} dangerouslySetInnerHTML={{ __html: p.replace(/\n/g, " ").replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") }} />;
          })}
        </div>
      </div>
      <InlineRevise rawOutput={rawOutput} language={language} onRevised={onRevised} systemPrompt={systemPrompt} />
    </div>
  );
}

// ─── HUMAN VERSION ───────────────────────────────────────────────────────────
function HumanVersion() {
  const [mode, setMode] = useState(MODES.FRESH);
  const [fields, setFields] = useState({
    topic: "", audience: "", wordCount: "800", language: "UK English",
    notes: "", referenceBlog: "", originalBlog: "", revisionInstructions: "",
    sourceMaterial: "", recruitmentSection: false, writingPerson: "generic",
  });
  const [loading, setLoading] = useState(false);
  const [blog, setBlog] = useState(null);
  const [error, setError] = useState("");
  const [rawOutput, setRawOutput] = useState("");
  const resultRef = useRef(null);
  const set = (key, val) => setFields(f => ({ ...f, [key]: val }));
  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  const handleGenerate = async () => {
    if (mode === MODES.REVISE && !fields.originalBlog) return setError("Please paste the blog you want to revise.");
    if (mode === MODES.REWRITE && !fields.referenceBlog) return setError("Please paste a reference blog.");
    setError(""); setLoading(true); setBlog(null);
    try {
      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-api-key": API_KEY, "anthropic-version": "2023-06-01", "anthropic-dangerous-direct-browser-access": "true" },
        body: JSON.stringify({ model: "claude-opus-4-6", max_tokens: 4000, system: buildHumanSystemPrompt(today), messages: [{ role: "user", content: buildHumanUserPrompt(mode, fields) }] }),
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error.message);
      const raw = data.content[0].text;
      setRawOutput(raw); setBlog(parseBlogOutput(raw));
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) { setError(e.message || "Something went wrong."); }
    finally { setLoading(false); }
  };

  const modeLabels = { [MODES.FRESH]: "Write Fresh Blog", [MODES.REWRITE]: "Rewrite from Source", [MODES.REVISE]: "Revise Existing Blog" };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 28 }}>
        {Object.entries(modeLabels).map(([key, label]) => (
          <button key={key} onClick={() => { setMode(key); setBlog(null); setError(""); }}
            style={{ flex: 1, padding: "12px 8px", borderRadius: 10, border: "2px solid", borderColor: mode === key ? "#0ea5e9" : "#ddd", background: mode === key ? "#0ea5e9" : "#fff", color: mode === key ? "#fff" : "#555", fontFamily: "sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ background: "#fff", borderRadius: 14, padding: 32, boxShadow: "0 2px 16px rgba(0,0,0,0.07)", border: "1px solid #e8e4dc", marginBottom: 24 }}>
        <div style={{ display: "flex", gap: 16, marginBottom: 22 }}>
          <div style={{ flex: 1 }}><Label>Language</Label>
            <select value={fields.language} onChange={e => set("language", e.target.value)} style={selectStyle}>{LANGUAGES.map(l => <option key={l}>{l}</option>)}</select>
          </div>
          {mode !== MODES.REVISE && <div style={{ flex: 1 }}><Label>Word Count</Label>
            <select value={fields.wordCount} onChange={e => set("wordCount", e.target.value)} style={selectStyle}>{WORD_COUNTS.map(w => <option key={w}>{w}</option>)}</select>
          </div>}
        </div>

        {mode !== MODES.REVISE && (
          <div style={{ marginBottom: 22 }}>
            <Label>Writing Style</Label>
            <div style={{ display: "flex", gap: 8 }}>
              {[["generic", "Generic (No I/We)"], ["first", "First Person (I/We)"]].map(([val, label]) => (
                <button key={val} onClick={() => set("writingPerson", val)}
                  style={{ flex: 1, padding: "10px 8px", borderRadius: 9, border: "2px solid", borderColor: fields.writingPerson === val ? "#0ea5e9" : "#ddd", background: fields.writingPerson === val ? "#0ea5e9" : "#fff", color: fields.writingPerson === val ? "#fff" : "#555", fontFamily: "sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}>
                  {label}
                </button>
              ))}
            </div>
            <div style={{ fontFamily: "sans-serif", fontSize: 11, color: "#999", marginTop: 6 }}>
              {fields.writingPerson === "first" ? "Writes as the consultant speaking personally — uses I, we, my" : "Authoritative industry voice — no personal pronouns"}
            </div>
          </div>
        )}

        {mode === MODES.FRESH && (
          <>
            <Field label="Blog Topic" placeholder="e.g. Why interim finance directors are in high demand right now" value={fields.topic} onChange={v => set("topic", v)} />
            <Field label="Target Audience" placeholder="e.g. CFOs and senior finance professionals" value={fields.audience} onChange={v => set("audience", v)} />
            <div onClick={() => set("recruitmentSection", !fields.recruitmentSection)}
              style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 16px", background: fields.recruitmentSection ? "#f0f4ff" : "#fafafa", border: `1.5px solid ${fields.recruitmentSection ? "#4a6fa5" : "#ddd"}`, borderRadius: 10, cursor: "pointer", marginBottom: 20, transition: "all 0.15s" }}>
              <div style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${fields.recruitmentSection ? "#4a6fa5" : "#bbb"}`, background: fields.recruitmentSection ? "#4a6fa5" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                {fields.recruitmentSection && <span style={{ color: "#fff", fontSize: 12, fontWeight: 900 }}>✓</span>}
              </div>
              <div>
                <div style={{ fontFamily: "sans-serif", fontSize: 13, fontWeight: 700, color: "#1a1a2e" }}>Include Recruitment Section</div>
                <div style={{ fontFamily: "sans-serif", fontSize: 12, color: "#777", marginTop: 2 }}>Adds a natural section covering hiring trends, in-demand skills, and salary ranges</div>
              </div>
            </div>
            <Field label="Angles or Points to Cover (optional)" placeholder="Any specific themes, stats, or talking points" value={fields.notes} onChange={v => set("notes", v)} textarea rows={3} />
            <div style={{ borderTop: "1px dashed #e0dbd0", margin: "8px 0 20px" }} />
            <Field label="Source Material (optional)" placeholder="Paste any article or content for the AI to draw from. Leave empty to write from its own knowledge." value={fields.sourceMaterial} onChange={v => set("sourceMaterial", v)} textarea rows={5} />
            {fields.sourceMaterial && <div style={{ background: "#fffbf0", border: "1px solid #f0e0b0", borderRadius: 8, padding: "10px 14px", marginTop: -10, marginBottom: 20, fontFamily: "sans-serif", fontSize: 12, color: "#8a6d20" }}>Source material detected — facts and data will be woven in naturally.</div>}
          </>
        )}

        {mode === MODES.REWRITE && (
          <>
            <Field label="Topic Override (optional)" placeholder="Leave blank to extract from source" value={fields.topic} onChange={v => set("topic", v)} />
            <Field label="Target Audience (optional)" placeholder="Leave blank to extract from source" value={fields.audience} onChange={v => set("audience", v)} />
            <Field label="Paste Reference Blog" placeholder="Paste source blog here — AI rewrites it completely in your agency's voice" value={fields.referenceBlog} onChange={v => set("referenceBlog", v)} textarea rows={8} required />
            <Field label="Additional Angles (optional)" placeholder="Any specific direction for the new blog" value={fields.notes} onChange={v => set("notes", v)} textarea rows={3} />
          </>
        )}

        {mode === MODES.REVISE && (
          <>
            <Field label="Paste Your Existing Blog" placeholder="Paste the blog you want to revise" value={fields.originalBlog} onChange={v => set("originalBlog", v)} textarea rows={8} required />
            <Field label="What Would You Like Changed?" placeholder='e.g. "The opening feels flat" or "Cut 200 words from the middle"' value={fields.revisionInstructions} onChange={v => set("revisionInstructions", v)} textarea rows={4} required />
          </>
        )}

        {error && <div style={{ background: "#fff4f4", border: "1px solid #f5c6c6", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontFamily: "sans-serif", fontSize: 13, color: "#c0392b" }}>{error}</div>}

        <button onClick={handleGenerate} disabled={loading}
          style={{ width: "100%", padding: "15px", background: loading ? "#888" : "#0ea5e9", color: "#fff", border: "none", borderRadius: 10, fontFamily: "sans-serif", fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", marginTop: 4 }}>
          {loading ? "Writing your blog..." : modeLabels[mode]}
        </button>
      </div>

      {loading && <div style={{ textAlign: "center", padding: "40px 0", fontFamily: "sans-serif", color: "#888", fontSize: 14 }}>
        <div style={{ width: 36, height: 36, border: "3px solid #e8e4dc", borderTop: "3px solid #38bdf8", borderRadius: "50%", margin: "0 auto 16px", animation: "spin 0.9s linear infinite" }} />
        Crafting your blog — this usually takes 20 to 40 seconds...
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>}

      {blog && <div ref={resultRef}><BlogOutput blog={blog} rawOutput={rawOutput} language={fields.language} onRevised={(r) => { setRawOutput(r); setBlog(parseBlogOutput(r)); }} systemPrompt={buildHumanSystemPrompt(today)} /></div>}
    </div>
  );
}

// ─── AI-FRIENDLY VERSION ─────────────────────────────────────────────────────
function AIFriendlyVersion() {
  const [fields, setFields] = useState({
    topic: "", audience: "", wordCount: "800", language: "UK English",
    notes: "", sourceMaterial: "", recruitmentSection: false,
  });
  const [loading, setLoading] = useState(false);
  const [blog, setBlog] = useState(null);
  const [error, setError] = useState("");
  const [rawOutput, setRawOutput] = useState("");
  const resultRef = useRef(null);
  const set = (key, val) => setFields(f => ({ ...f, [key]: val }));
  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  const handleGenerate = async () => {
    if (!fields.topic) return setError("Please enter a blog topic.");
    setError(""); setLoading(true); setBlog(null);
    try {
      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-api-key": API_KEY, "anthropic-version": "2023-06-01", "anthropic-dangerous-direct-browser-access": "true" },
        body: JSON.stringify({ model: "claude-opus-4-6", max_tokens: 4000, system: buildAISystemPrompt(today), messages: [{ role: "user", content: buildAIUserPrompt(fields) }] }),
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error.message);
      const raw = data.content[0].text;
      setRawOutput(raw); setBlog(parseBlogOutput(raw));
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) { setError(e.message || "Something went wrong."); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 12, padding: "14px 18px", marginBottom: 24, fontFamily: "sans-serif", fontSize: 13, color: "#0369a1" }}>
        <strong>AI-Friendly Mode</strong> — Blogs are structured for AI answer engines (ChatGPT, Perplexity, Google AI Overviews), voice search, and featured snippets. Includes question headings, key takeaways, how-to steps, TL;DR and FAQ sections.
      </div>

      <div style={{ background: "#fff", borderRadius: 14, padding: 32, boxShadow: "0 2px 16px rgba(0,0,0,0.07)", border: "1px solid #e8e4dc", marginBottom: 24 }}>
        <div style={{ display: "flex", gap: 16, marginBottom: 22 }}>
          <div style={{ flex: 1 }}><Label>Language</Label>
            <select value={fields.language} onChange={e => set("language", e.target.value)} style={selectStyle}>{LANGUAGES.map(l => <option key={l}>{l}</option>)}</select>
          </div>
          <div style={{ flex: 1 }}><Label>Word Count</Label>
            <select value={fields.wordCount} onChange={e => set("wordCount", e.target.value)} style={selectStyle}>{WORD_COUNTS.map(w => <option key={w}>{w}</option>)}</select>
          </div>
        </div>

        <Field label="Blog Topic *" placeholder="e.g. How to hire a data engineer in 2026" value={fields.topic} onChange={v => set("topic", v)} required />
        <Field label="Target Audience" placeholder="e.g. HR managers and technical hiring teams" value={fields.audience} onChange={v => set("audience", v)} />

        <div onClick={() => set("recruitmentSection", !fields.recruitmentSection)}
          style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 16px", background: fields.recruitmentSection ? "#f0f9ff" : "#fafafa", border: `1.5px solid ${fields.recruitmentSection ? "#0ea5e9" : "#ddd"}`, borderRadius: 10, cursor: "pointer", marginBottom: 20, transition: "all 0.15s" }}>
          <div style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${fields.recruitmentSection ? "#0ea5e9" : "#bbb"}`, background: fields.recruitmentSection ? "#0ea5e9" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
            {fields.recruitmentSection && <span style={{ color: "#fff", fontSize: 12, fontWeight: 900 }}>✓</span>}
          </div>
          <div>
            <div style={{ fontFamily: "sans-serif", fontSize: 13, fontWeight: 700, color: "#1a1a2e" }}>Include Recruitment Section</div>
            <div style={{ fontFamily: "sans-serif", fontSize: 12, color: "#777", marginTop: 2 }}>Adds a "What does this mean for your career?" section with hiring trends and salary data</div>
          </div>
        </div>

        <Field label="Specific Angles or Keywords (optional)" placeholder="Any topics, questions or keywords to focus on" value={fields.notes} onChange={v => set("notes", v)} textarea rows={3} />
        <div style={{ borderTop: "1px dashed #e0dbd0", margin: "8px 0 20px" }} />
        <Field label="Source Material (optional)" placeholder="Paste any article or content for the AI to draw real facts and examples from." value={fields.sourceMaterial} onChange={v => set("sourceMaterial", v)} textarea rows={5} />

        {error && <div style={{ background: "#fff4f4", border: "1px solid #f5c6c6", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontFamily: "sans-serif", fontSize: 13, color: "#c0392b" }}>{error}</div>}

        <button onClick={handleGenerate} disabled={loading}
          style={{ width: "100%", padding: "15px", background: loading ? "#888" : "#0369a1", color: "#fff", border: "none", borderRadius: 10, fontFamily: "sans-serif", fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", marginTop: 4 }}>
          {loading ? "Generating AI-friendly blog..." : "Generate AI-Friendly Blog"}
        </button>
      </div>

      {loading && <div style={{ textAlign: "center", padding: "40px 0", fontFamily: "sans-serif", color: "#888", fontSize: 14 }}>
        <div style={{ width: 36, height: 36, border: "3px solid #e8e4dc", borderTop: "3px solid #0369a1", borderRadius: "50%", margin: "0 auto 16px", animation: "spin 0.9s linear infinite" }} />
        Structuring your AI-friendly blog...
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>}

      {blog && <div ref={resultRef}><BlogOutput blog={blog} rawOutput={rawOutput} language={fields.language} onRevised={(r) => { setRawOutput(r); setBlog(parseBlogOutput(r)); }} systemPrompt={buildAISystemPrompt(today)} /></div>}
    </div>
  );
}

// ─── MAIN APP ────────────────────────────────────────────────────────────────
export default function App() {
  const [version, setVersion] = useState("human");

  return (
    <div style={{ minHeight: "100vh", background: "#f7f5f0", fontFamily: "'Georgia', serif" }}>
      {/* Header */}
      <div style={{ background: "#000000", padding: "22px 40px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 38, height: 38, background: "#38bdf8", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#000", fontWeight: 900, fontSize: 18, fontFamily: "sans-serif" }}>A</span>
          </div>
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 18, letterSpacing: 0.3 }}>AIVAN</div>
            <div style={{ color: "#38bdf8", fontSize: 12, fontFamily: "sans-serif", letterSpacing: 1, textTransform: "uppercase" }}>AI Blog Writer</div>
          </div>
        </div>

        {/* Version switcher in header */}
        <div style={{ display: "flex", gap: 4, background: "#1a1a1a", borderRadius: 10, padding: 4 }}>
          {[["human", "Human Version"], ["ai", "AI-Friendly"]].map(([key, label]) => (
            <button key={key} onClick={() => setVersion(key)}
              style={{ padding: "8px 18px", borderRadius: 7, border: "none", fontFamily: "sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
                background: version === key ? (key === "human" ? "#38bdf8" : "#0369a1") : "transparent",
                color: version === key ? "#000" : "#888" }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 780, margin: "0 auto", padding: "36px 24px 60px" }}>
        {version === "human" ? <HumanVersion /> : <AIFriendlyVersion />}
      </div>
    </div>
  );
}

function Label({ children }) {
  return <div style={{ fontFamily: "sans-serif", fontSize: 12, fontWeight: 700, color: "#444", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 7 }}>{children}</div>;
}

function Field({ label, placeholder, value, onChange, textarea, rows = 2, required }) {
  const base = { width: "100%", padding: "11px 14px", borderRadius: 9, border: "1.5px solid #ddd", fontSize: 14, fontFamily: "sans-serif", outline: "none", color: "#1a1a2e", background: "#fafafa", resize: "vertical", boxSizing: "border-box" };
  return (
    <div style={{ marginBottom: 20 }}>
      <Label>{label}{required && <span style={{ color: "#0ea5e9", marginLeft: 4 }}>*</span>}</Label>
      {textarea
        ? <textarea rows={rows} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} style={base} />
        : <input type="text" placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} style={{ ...base, resize: "none" }} />}
    </div>
  );
}

const selectStyle = { width: "100%", padding: "11px 14px", borderRadius: 9, border: "1.5px solid #ddd", fontSize: 14, fontFamily: "sans-serif", background: "#fafafa", color: "#1a1a2e", outline: "none", cursor: "pointer" };
